import { ChevronRight, ChevronDown, type LucideIcon } from 'lucide-react';
import { Link } from 'components/ui/link';
import { useLocation } from 'react-router-dom';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from 'components/ui/collapsible';
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from 'components/ui/sidebar';
import { User } from 'types/api';

type SubMenuItem = {
    title: string;
    url: string;
    items?: TertiaryMenuItem[];
};

type TertiaryMenuItem = {
    title: string;
    url: string;
};

type NavItem = {
    title: string;
    url: string;
    icon: LucideIcon;
    items?: SubMenuItem[];
    condition?: (user: User | undefined) => boolean;
};

const SubMenuItems = ({
    items,
    isActive,
}: {
    items: SubMenuItem[];
    isActive: (url: string) => boolean;
}) => {
    return (
        <SidebarMenuSub>
            {items.map((subItem) => (
                <SidebarMenuSubItem key={subItem.title}>
                    <Collapsible
                        key={subItem.title}
                        asChild
                        defaultOpen={isActive(subItem.url)}
                    >
                        <>
                            <SidebarMenuSubButton
                                asChild
                                isActive={isActive(subItem.url)}
                            >
                                <Link to={subItem.url}>
                                    <span>{subItem.title}</span>
                                </Link>
                            </SidebarMenuSubButton>
                        </>
                    </Collapsible>
                </SidebarMenuSubItem>
            ))}
        </SidebarMenuSub>
    );
};

export function NavMain({
    items,
    user,
}: {
    items: NavItem[];
    user: User | undefined;
}) {
    const location = useLocation();

    const isActive = (url: string) => location.pathname.startsWith(url);

    const filteredItems = items.filter((item) =>
        item.condition ? item.condition(user) : true,
    );

    return (
        <SidebarGroup>
            <SidebarGroupLabel>Platform</SidebarGroupLabel>
            <SidebarMenu>
                {filteredItems.map((item) => (
                    <Collapsible
                        key={item.title}
                        asChild
                        defaultOpen={isActive(item.url)}
                        className="group/collapsible"
                    >
                        <SidebarMenuItem>
                            {!item.items?.length ? (
                                <SidebarMenuButton
                                    asChild
                                    isActive={isActive(item.url)}
                                >
                                    <Link to={item.url}>
                                        <item.icon />
                                        <span>{item.title}</span>
                                    </Link>
                                </SidebarMenuButton>
                            ) : (
                                <>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuButton
                                            isActive={isActive(item.url)}
                                        >
                                            {item.title}{' '}
                                            <ChevronRight className="ml-auto group-data-[state=open]/collapsible:hidden" />
                                            <ChevronDown className="ml-auto group-data-[state=closed]/collapsible:hidden" />
                                        </SidebarMenuButton>
                                    </CollapsibleTrigger>

                                    <CollapsibleContent>
                                        <SubMenuItems
                                            items={item.items}
                                            isActive={isActive}
                                        />
                                    </CollapsibleContent>
                                </>
                            )}
                        </SidebarMenuItem>
                    </Collapsible>
                ))}
            </SidebarMenu>
        </SidebarGroup>
    );
}
